import React from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import { PropTypes as PT } from 'prop-types';
import { nucleusConstants } from '@lifesize/nucleus';
import _compact from 'lodash/compact';
import _isFunction from 'lodash/isFunction';
import _noop from 'lodash/noop';
import { ReactComponent as CancelIcon } from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import { ReactComponent as PeopleIcon } from '@lifesize/ux-assets/vectors/svgIcons/people.svg';
import { ReactComponent as OtmIcon } from '@lifesize/ux-assets/vectors/svgIcons/hourglass.svg';
import Badge from 'components/Badge/Badge';
import Tooltip from 'components/Tooltip/Tooltip';
import classes from 'components/Chat/ChatContactItem.module.scss';
import _get from 'lodash/get';

const messages = defineMessages({
  unknownUser: {
    id: 'unknownUser',
    defaultMessage: 'Unknown User'
  }
});

function renderStatus(contact) {
  if (contact && contact.isMeeting) {
    if (contact.meetingType === nucleusConstants.MEETING_TYPE_ONE_TIME) {
      return <OtmIcon data-testid="otmIcon" />;
    }

    return <PeopleIcon data-testid="peopleIcon" />;
  }
  return <div className={`${classes.availability} ${contact.isOnline ? classes.available : ''}`} />;
}

export class ChatContactItem extends React.Component {
  componentDidMount() {
    if (this.props.isSelected && this.props.conversation.unreadMessageCount && this.props.markConversationAsRead) {
      this.props.markConversationAsRead(this.props.conversation);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.isSelected &&
      this.props.conversation.unreadMessageCount !== prevProps.conversation.unreadMessageCount &&
      this.props.conversation.unreadMessageCount > 0
    ) {
      this.props.markConversationAsRead(this.props.conversation);
    }
  }

  removeConversation = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.onRemove(this.props.contact);
  };

  render() {
    const { canRemove, canSelect, contact, isSelected, onSelect, conversation, intl } = this.props;

    const clickHandler = _isFunction(onSelect) ? onSelect : _noop;
    const itemClassName = _compact([
      classes.chatContactItem,
      _isFunction(onSelect) ? classes.isClickable : '',
      isSelected && canSelect ? classes.selected : ''
    ]).join(' ');

    const name = _get(contact, 'displayName') || _get(contact, 'name') || intl.formatMessage(messages.unknownUser);

    return (
      <li
        id="chatContactListItem"
        className={itemClassName}
        onClick={() => clickHandler(conversation)}
        data-testid={contact.isMeeting ? 'chat-list-item-meeting' : 'chat-list-item-contact'}
      >
        {name.length > 16 && <Tooltip label={name} className={classes.wrapTooltip} />}
        <div className={classes.status}>{renderStatus(contact)}</div>
        <div className={classes.label}>{name}</div>
        {canRemove ? (
          <div className={classes.buttons}>
            {
              // Out-of-call meetings for any user type are no longer showing badges (CA-2042)
              conversation.unreadMessageCount && !conversation.contact?.isMeeting ? (
                <div className={classes.badgeContainer}>
                  <Badge count={conversation.unreadMessageCount} className={classes.badge} />
                </div>
              ) : null
            }
            <button className={classes.remove} data-testid="cancelIconButton" onClick={this.removeConversation}>
              <CancelIcon data-testid="cancelIcon" />
            </button>
          </div>
        ) : null}
      </li>
    );
  }
}

ChatContactItem.propTypes = {
  canRemove: PT.bool,
  canSelect: PT.bool,
  contact: PT.object,
  isSelected: PT.bool,
  onRemove: PT.func,
  onSelect: PT.func,
  conversation: PT.object,
  markConversationAsRead: PT.func,
  intl: PT.object
};

ChatContactItem.defaultProps = {
  canRemove: true,
  canSelect: true,
  contact: {},
  conversation: {}
};

export default injectIntl(ChatContactItem);
